import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from "@mui/material";
import { findAllPages } from "../../../API/apiPages";
import { findRole } from "../../../API/apiRoles";
import {
  findAllPermissionActions,
  findRoleActionPermissions,
  saveRoleActionPermissions,
} from "../../../API/apiPermissionActions";
import {
  findPermissionsByRole,
  savePermissions,
} from "../../../API/apiPermissions";
import { SystemPage } from "../../../types/SystemPage";
import { PermissionAction } from "../../../types/SystemActionPermission";
import styles from "./GestaoPerfis.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_general from "../../../styles/General.module.css";
import styles_table from "../../../styles/Table.module.css";
import styles_grid from "../../../styles/Grid.module.css";

const PermissoesPerfil = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleIdParam = searchParams.get("role_id");
  const roleId = Number(roleIdParam);

  const [roleName, setRoleName] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [pages, setPages] = useState<SystemPage[]>([]);
  const [permissionActions, setPermissionActions] = useState<PermissionAction[]>([]);
  const [selectedPageIds, setSelectedPageIds] = useState<Set<number>>(new Set());
  const [selectedPermissionActionIds, setSelectedPermissionActionIds] =
    useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);

  useEffect(() => {
    if (!roleId || Number.isNaN(roleId)) {
      setMsgError("Perfil inválido para configuração de permissões.");
      return;
    }

    loadData(roleId);
  }, [roleId]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  const selectedCount = useMemo(() => selectedPageIds.size, [selectedPageIds]);
  const selectedActionCount = useMemo(
    () => selectedPermissionActionIds.size,
    [selectedPermissionActionIds],
  );

  const groupedPages = useMemo(() => {
    return pages.reduce<Record<string, SystemPage[]>>((acc, page) => {
      const moduleName = page.module?.name || "Sem módulo";
      if (!acc[moduleName]) acc[moduleName] = [];
      acc[moduleName].push(page);
      return acc;
    }, {});
  }, [pages]);

  const groupedPermissionActions = useMemo(() => {
    return permissionActions.reduce<Record<string, PermissionAction[]>>(
      (acc, permissionAction) => {
        const moduleName =
          typeof permissionAction.module === "string"
            ? permissionAction.module
            : permissionAction.module?.name || "Sem módulo";
        if (!acc[moduleName]) acc[moduleName] = [];
        acc[moduleName].push(permissionAction);
        return acc;
      },
      {},
    );
  }, [permissionActions]);

  const loadAllPages = async () => {
    const perPage = 100;
    let currentPage = 1;
    let lastPage = 1;
    const allPages: SystemPage[] = [];

    do {
      const response = await findAllPages(currentPage, perPage);
      allPages.push(...(response.pages || []));
      lastPage = response.pagination?.last_page || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return allPages;
  };

  const loadData = async (currentRoleId: number) => {
    setLoading(true);
    try {
      const [
        permissionsResponse,
        pagesResponse,
        roleResponse,
        actionsResponse,
        roleActionsResponse,
      ] = await Promise.all([
        findPermissionsByRole(currentRoleId),
        loadAllPages(),
        findRole(currentRoleId),
        findAllPermissionActions(),
        findRoleActionPermissions(currentRoleId),
      ]);

      setRoleName(
        roleResponse?.nome ||
          permissionsResponse.permissions?.[0]?.role?.nome ||
          roleActionsResponse.data.role?.nome ||
          "",
      );
      setPages(pagesResponse);
      setPermissionActions(actionsResponse.data || []);
      setSelectedPageIds(new Set(permissionsResponse.page_ids || []));
      setSelectedPermissionActionIds(
        new Set(roleActionsResponse.data.permission_action_ids || []),
      );
    } catch (error) {
      setMsgError("Não foi possível carregar as permissões do perfil.");
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePage = (pageId: number) => {
    setSelectedPageIds((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  };

  const handleTogglePermissionAction = (permissionActionId: number) => {
    setSelectedPermissionActionIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionActionId)) {
        next.delete(permissionActionId);
      } else {
        next.add(permissionActionId);
      }
      return next;
    });
  };

  const handleToggleAllModulePages = (modulePages: SystemPage[]) => {
    const allSelected = modulePages.every((page) => selectedPageIds.has(page.id));

    setSelectedPageIds((prev) => {
      const next = new Set(prev);
      modulePages.forEach((page) => {
        if (allSelected) {
          next.delete(page.id);
        } else {
          next.add(page.id);
        }
      });
      return next;
    });
  };

  const handleToggleAllModuleActions = (
    modulePermissionActions: PermissionAction[],
  ) => {
    const allSelected = modulePermissionActions.every((permissionAction) =>
      selectedPermissionActionIds.has(permissionAction.id),
    );

    setSelectedPermissionActionIds((prev) => {
      const next = new Set(prev);
      modulePermissionActions.forEach((permissionAction) => {
        if (allSelected) {
          next.delete(permissionAction.id);
        } else {
          next.add(permissionAction.id);
        }
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!roleId || Number.isNaN(roleId)) return;

    try {
      setLoadingSave(true);
      await Promise.all([
        savePermissions({
          role_id: roleId,
          page_ids: Array.from(selectedPageIds),
        }),
        saveRoleActionPermissions(roleId, {
          permission_action_ids: Array.from(selectedPermissionActionIds),
        }),
      ]);
      setMsgSuccess("Permissões atualizadas com sucesso.");
    } catch (error) {
      setMsgError("Não foi possível atualizar as permissões.");
    } finally {
      setLoadingSave(false);
    }
  };

  const renderPagesTab = () => {
    if (pages.length === 0) {
      return (
        <TableContainer className={styles_table.tableContainer}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell align="center" className={styles_table.textXs}>
                  Nenhuma página encontrada
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return Object.entries(groupedPages).map(([moduleName, modulePages]) => {
      const allSelected = modulePages.every((page) => selectedPageIds.has(page.id));
      const hasSomeSelected = modulePages.some((page) =>
        selectedPageIds.has(page.id),
      );

      return (
        <Card key={moduleName} className={styles_general.mb4}>
          <CardContent>
            <div className={`${styles_grid.displayFlexBetween} ${styles_grid.itemsCenter}`}>
              <div className={styles_general.titlePrimary}>{moduleName}</div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={hasSomeSelected && !allSelected}
                    onChange={() => handleToggleAllModulePages(modulePages)}
                  />
                }
                label="Selecionar todos"
              />
            </div>

            <TableContainer className={styles_table.tableContainer}>
              <Table size="small">
                <TableHead className={styles_table.tableHead}>
                  <TableRow>
                    <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Permitir</TableCell>
                    <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Página</TableCell>
                    <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Rota</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modulePages.map((page, index) => (
                    <TableRow
                      key={page.id}
                      className={`${styles_table.tableRow} ${index % 2 === 0 ? styles_table.tableRowEven : styles_table.tableRowOdd}`}
                    >
                      <TableCell padding="none" align="center">
                        <Checkbox
                          checked={selectedPageIds.has(page.id)}
                          onChange={() => handleTogglePage(page.id)}
                        />
                      </TableCell>
                      <TableCell padding="none" className={styles_table.textXs}>
                        {page.name}
                      </TableCell>
                      <TableCell padding="none" className={styles_table.textXs}>
                        {page.route}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      );
    });
  };

  const renderActionsTab = () => {
    if (permissionActions.length === 0) {
      return (
        <TableContainer className={styles_table.tableContainer}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell align="center" className={styles_table.textXs}>
                  Nenhuma ação encontrada
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return Object.entries(groupedPermissionActions).map(
      ([moduleName, modulePermissionActions]) => {
        const allSelected = modulePermissionActions.every((permissionAction) =>
          selectedPermissionActionIds.has(permissionAction.id),
        );
        const hasSomeSelected = modulePermissionActions.some((permissionAction) =>
          selectedPermissionActionIds.has(permissionAction.id),
        );

        return (
          <Card key={moduleName} className={styles_general.mb4}>
            <CardContent>
              <div className={`${styles_grid.displayFlexBetween} ${styles_grid.itemsCenter}`}>
                <div className={styles_general.titlePrimary}>{moduleName}</div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={hasSomeSelected && !allSelected}
                      onChange={() =>
                        handleToggleAllModuleActions(modulePermissionActions)
                      }
                    />
                  }
                  label="Selecionar todos"
                />
              </div>

              <TableContainer className={styles_table.tableContainer}>
                <Table size="small">
                  <TableHead className={styles_table.tableHead}>
                    <TableRow>
                      <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Permitir</TableCell>
                      <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Label</TableCell>
                      <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Key</TableCell>
                      <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modulePermissionActions.map((permissionAction, index) => (
                      <TableRow
                        key={permissionAction.id}
                        className={`${styles_table.tableRow} ${index % 2 === 0 ? styles_table.tableRowEven : styles_table.tableRowOdd}`}
                      >
                        <TableCell padding="none" align="center">
                          <Checkbox
                            checked={selectedPermissionActionIds.has(permissionAction.id)}
                            onChange={() =>
                              handleTogglePermissionAction(permissionAction.id)
                            }
                          />
                        </TableCell>
                        <TableCell padding="none" className={styles_table.textXs}>
                          {permissionAction.label}
                        </TableCell>
                        <TableCell padding="none" className={styles_table.textXs}>
                          {permissionAction.key}
                        </TableCell>
                        <TableCell padding="none" className={styles_table.textXs}>
                          {permissionAction.action}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        );
      },
    );
  };

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>
          Permissões do Perfil {roleName ? `- ${roleName}` : ""}
        </div>

        {msgSuccess && (
          <div className={styles_table.message}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        {msgError && (
          <div className={styles_table.message}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        {loading ? (
          <div className={styles_general.mt6}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              className={styles_general.mb4}
            >
              <Tab label="Permissões de Telas" />
              <Tab label="Permissões de Ações" />
            </Tabs>

            {activeTab === 0 && renderPagesTab()}
            {activeTab === 1 && renderActionsTab()}
          </>
        )}

        <div className={`${styles_general.mt6} ${styles_grid.displayFlexBetween} ${styles_grid.itemsCenter}`}>
          <div className={styles_table.textXs}>
            {selectedCount} página(s) e {selectedActionCount} ação(ões) selecionada(s)
          </div>
          <div>
            <Button
              variant="contained"
              className={styles_button.buttonSquareSecondary}
              onClick={() => navigate("/perfis")}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              className={`${styles_button.buttonSquarePrimary} ${loadingSave ? styles_button.buttonOpacity : ""}`}
              onClick={handleSave}
              disabled={loadingSave}
            >
              {loadingSave ? "Salvando..." : "Salvar Permissões"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PermissoesPerfil;
